import { memo } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { authRolesSelector } from '../pages/Login'

export type RouteGuardProps = {
  /**
   * Indicates whether or not this route requires authenticated user
   */
  _auth?: boolean
  /**
   * List of roles that can access this route (user needs at least one).
   * Admin role always has access to all routes.
   */
  allowedRoles?: string[]
}

/**
 * App route guard
 *
 * Role-based access:
 * - Admin: Full access to everything
 * - User_landdev: Explore + Analysis
 * - User_brokerinfo: Explore only
 * - User_estimate: Explore + Estimate
 */
const RouteGuard = ({ allowedRoles }: RouteGuardProps) => {
  const roles = useSelector(authRolesSelector)
  const location = useLocation()
  const isLoggedIn = !!roles.length

  // Check if we're on the login page
  const isLoginPage = location.pathname === '/login'

  // If user is logged in and trying to access login page, redirect
  if (isLoggedIn && isLoginPage) {
    // Get the redirect URL from query params
    const searchParams = new URLSearchParams(location.search)
    const from = searchParams.get('from') || '/developer-map'
    return <Navigate to={from} replace />
  }

  // If user is not logged in and not on login page, redirect to login
  if (!isLoggedIn && !isLoginPage) {
    return (
      <Navigate
        to={{
          pathname: '/login',
          search: `?from=${window.location.pathname}${window.location.search}`,
        }}
        replace
      />
    )
  }

  // Handle role-based access for logged-in users
  if (isLoggedIn && allowedRoles && allowedRoles.length > 0) {
    // Admin always has access
    const isAdmin = roles.includes('Admin')

    // Check if user has at least one of the allowed roles
    const hasRequiredRole = allowedRoles.some((role) => roles.includes(role))

    if (!isAdmin && !hasRequiredRole) {
      // Redirect to developer-map (Explore) which all roles can access
      return <Navigate to={{ pathname: '/developer-map' }} replace />
    }
  }

  return <Outlet />
}

export default memo(RouteGuard)
